import { requestHeaders } from '@/api';
import { API_URL } from '@/data/service/dataService';
import { toast } from '@montugroup/design-system';
import { Box, Stack, Typography, styled } from '@mui/material';
import Button from '@mui/material/Button';
import { grey } from '@mui/material/colors';
import { useState } from 'react';
import type { DassAssessmentDetails, DassAssessmentScoreDetail } from '../MedicalCondition.types';

interface PatientInfo {
  patientId: number | null;
  patientFirstName: string | null | undefined;
  patientEmail: string | null | undefined;
}

type ResendDassAssessmentParams = {
  userEmail?: string;
  fetchDassAssessment: () => void;
} & Pick<PatientInfo, 'patientId' | 'patientEmail'> & {
    patientName: PatientInfo['patientFirstName'];
  };

interface DassAssessmentScoreStatusProps {
  dassAssessmentDetails?: DassAssessmentDetails;
  dassAssessmentScoreDetail?: DassAssessmentScoreDetail;
  fetchDassAssessment: () => void;
  userEmail?: string;
  isDoc: boolean;
  disabled: boolean;
  patientInfo: PatientInfo;
}

const StyleFieldset = styled(Box)({
  borderRadius: '4px',
  border: `1px solid ${grey[400]}`,
  padding: `0 8px`,
});

const StyledResendButton = styled(Button)({
  textTransform: 'capitalize',
  padding: 0,
  textDecoration: 'underline',

  '&:hover': {
    // ideally we shouldn't use !important here but due to the specificity
    // of the brand theming theres no way to override them
    backgroundColor: 'transparent !important',
    padding: '0 !important',
    boxShadow: 'none',
    textDecoration: 'underline',
  },
});

const StyledDl = styled('dl')({
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
});

const resendDassAssessment = async ({
  userEmail,
  patientId,
  patientName,
  patientEmail,
  fetchDassAssessment,
}: ResendDassAssessmentParams) => {
  const sendBody = {
    userEmail,
    patientId,
    patientEmail,
    patientName,
    daysLinkValid: 5,
  };

  try {
    const response = await fetch(`${API_URL}/dass/updateDass`, {
      method: 'PUT',
      headers: requestHeaders(),
      body: JSON.stringify(sendBody),
    });

    if (response.ok) {
      toast.success('Sent Dass Assessment Email');
      await fetchDassAssessment();
    } else {
      toast.error('Failed to Send Dass Assessment Email');
    }
  } catch (_error) {
    toast.error('Failed to Send Dass Assessment Email');
  }
};

const DassAssessmentScoreStatus = ({
  userEmail,
  dassAssessmentDetails,
  dassAssessmentScoreDetail,
  fetchDassAssessment,
  isDoc,
  disabled,
  patientInfo,
}: DassAssessmentScoreStatusProps) => {
  const [resendEmailClicked, setResendEmailClicked] = useState(false);
  const { patientId, patientEmail, patientFirstName: patientName } = patientInfo;

  const handleResendDassEmail = async () => {
    if (resendEmailClicked) {
      return;
    }

    setResendEmailClicked(true);

    await resendDassAssessment({
      userEmail,
      patientId,
      patientEmail,
      patientName,
      fetchDassAssessment,
    });
  };

  return (
    <Box marginBottom={3}>
      <StyleFieldset component="fieldset">
        <legend>
          <Typography component="label" sx={{ fontSize: '0.8rem', padding: '5px', color: 'text.secondary' }}>
            DASS Assessment Score
          </Typography>
        </legend>
        <Stack sx={{ px: 2, pb: 2 }}>
          {!dassAssessmentDetails?.is_submitted && (
            <Stack alignItems="start" data-testid="yet_to_be_completed">
              <Typography>Yet to be completed</Typography>
              {!isDoc && (
                <StyledResendButton
                  data-testid="resend-email"
                  disabled={disabled}
                  onClick={handleResendDassEmail}
                  variant="text"
                >
                  Resend email
                </StyledResendButton>
              )}
            </Stack>
          )}
          {(dassAssessmentDetails?.is_submitted || dassAssessmentScoreDetail?.anxiety?.level) && (
            <Stack data-testid="dass-assessment-score" direction="column" sx={{ width: '100%' }}>
              <StyledDl>
                <dt>Depression</dt>
                <dd>{dassAssessmentScoreDetail?.depression?.score}</dd>
              </StyledDl>
              <StyledDl>
                <dt>Anxiety</dt>
                <dd>{dassAssessmentScoreDetail?.anxiety?.score}</dd>
              </StyledDl>
              <StyledDl>
                <dt>Stress</dt>
                <dt>{dassAssessmentScoreDetail?.stress?.score}</dt>
              </StyledDl>
            </Stack>
          )}
        </Stack>
      </StyleFieldset>
    </Box>
  );
};

export default DassAssessmentScoreStatus;
